import { UUID } from "@bitory-gmbh/nidavellir";
import { DocumentKind, ServiceKind } from "@bitory-gmbh/bsm";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  UpdateRequest,
  useConnectDocumentDataCreateMutation,
  useConnectDocumentDataShowQuery,
  useConnectDocumentDataUpdateMutation,
} from "@/services/backend/connect/api/service";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { DocumentData } from "@/services/backend/connect/types/document-data";
import { TextInput } from "@/shared/components/form/text-input";
import { useEffect, useState } from "react";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  is404,
  parseRTKQueryErrorToSonner,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { v4 } from "uuid";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export function ConnectDocumentDataCard({
  serviceKind,
  documentKind,
  documentId,
  disabled,
}: {
  serviceKind: ServiceKind;
  documentKind: DocumentKind;
  documentId: UUID;
  disabled?: boolean;
}) {
  const {
    data: documentData,
    isLoading,
    error,
    refetch,
  } = useConnectDocumentDataShowQuery({
    serviceKind,
    documentKind,
    documentId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Connect Daten")}</CardTitle>
      </CardHeader>
      <CardContent>
        {is404(error) && (
          <CreateConnectDataButton
            serviceKind={serviceKind}
            documentKind={documentKind}
            documentId={documentId}
            disabled={disabled}
            onSuccess={refetch}
          />
        )}
        {error && !is404(error) && <RTKQueryErrorAlert error={error} />}
        {isLoading && <CardContentSkeleton />}
        {documentData && (
          <DocumentDataFields documentData={documentData} disabled={disabled} />
        )}
      </CardContent>
    </Card>
  );
}

function DocumentDataFields({
  documentData,
  disabled,
}: {
  documentData: DocumentData;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateRequest>({
    id: documentData.id,
    number: documentData.number,
    customerNumber: documentData.customerNumber,
    objectNumber: documentData.objectNumber,
  });
  const mutation = useConnectDocumentDataUpdateMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: documentData.id,
    number: documentData.number,
    customerNumber: documentData.customerNumber,
    objectNumber: documentData.objectNumber,
  });

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <TextInput
        label={t("Nummer")}
        field="number"
        text={request.number}
        onTextChange={(number) => setRequest({ ...request, number })}
        errors={inputValidationErrorMessages(error, "number")}
        disabled={disabled}
      />
      <TextInput
        label={t("Kundennummer")}
        field="customerNumber"
        text={request.customerNumber}
        onTextChange={(customerNumber) =>
          setRequest({ ...request, customerNumber })
        }
        errors={inputValidationErrorMessages(error, "customerNumber")}
        disabled={disabled}
      />
      <TextInput
        label={t("Objektnummer")}
        field="objectNumber"
        text={request.objectNumber}
        onTextChange={(objectNumber) =>
          setRequest({ ...request, objectNumber })
        }
        errors={inputValidationErrorMessages(error, "objectNumber")}
        disabled={disabled}
      />
    </div>
  );
}

function CreateConnectDataButton({
  serviceKind,
  documentKind,
  documentId,
  disabled,
  onSuccess,
}: {
  serviceKind: ServiceKind;
  documentKind: DocumentKind;
  documentId: UUID;
  disabled?: boolean;
  onSuccess?: () => void;
}) {
  const [post, { isLoading, isSuccess, error }] =
    useConnectDocumentDataCreateMutation();

  const doCreate = () => {
    if (isLoading) return;

    post({ id: v4(), serviceKind, documentKind, documentId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success("Connect Daten Erfassung aktiviert.");
      onSuccess?.();
    }
  }, [isSuccess, onSuccess]);

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
    }
  }, [error]);

  return (
    <Button onClick={doCreate} disabled={isLoading || disabled}>
      {isLoading && <RefreshCw className="mr-2 animate-spin" />}
      {t("Connect Daten erfassen")}
    </Button>
  );
}
